import { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';
import { useSession, ensureUserHasOrg } from './auth';
import { setSentryOrg } from './sentry';

export type Role = 'owner' | 'admin' | 'member' | 'viewer';

export type OrgRoleValue = 'runs_jobs' | 'works_for_others' | 'both';

export interface OrgMembership {
  org_id: string;
  org_name: string;
  role: Role;                                 // member role within the org
  logo_path: string | null;
  /** Org-level profile (set by the new onboarding wizard) */
  org_role: OrgRoleValue | null;              // how the customer normally works on site
  trades: string[];                           // trade keys, multi-select
  risky_activities: string[];                 // 'height' | 'excavation' etc.
  profile_completed: boolean;
  enabled_features: string[];                 // explicit overrides — turned ON
  disabled_features: string[];                // explicit overrides — turned OFF
}

// localStorage key for cached org membership. We cache per-user so
// switching accounts on the same device doesn't surface another user's
// org for the brief moment before the fresh fetch returns.
const ORG_CACHE_KEY = 'hs:org-cache:v1';

interface OrgCacheEntry { userId: string; org: OrgMembership; }

function readCachedOrg(userId: string): OrgMembership | null {
  try {
    const raw = localStorage.getItem(ORG_CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as OrgCacheEntry;
    return entry.userId === userId ? entry.org : null;
  } catch { return null; }
}

function writeCachedOrg(userId: string, org: OrgMembership): void {
  try {
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({ userId, org }));
  } catch { /* quota or disabled — fine, we just won't get the speed-up */ }
}

/**
 * Loads the current user's primary organisation.
 *
 * Stale-while-revalidate: on mount, if we have a previously-cached org
 * for this user in localStorage, we render with it IMMEDIATELY (loading=
 * false on first paint) and refetch in the background to swap in fresh
 * data when it lands. First-ever load (cold cache) falls back to the
 * old behaviour — loading=true until the network returns.
 *
 * This is the single biggest UX lever in the app: useUserOrg is called
 * by basically every page, so making it instant on revisit makes the
 * whole app feel instant.
 *
 * Auto-creates an org from `pending_org_name` metadata on first visit,
 * then refetches whenever the caller invokes `refresh()`.
 */
export function useUserOrg() {
  const { session } = useSession();
  // Hydrate from cache on first render if we have a cached org for this
  // user. Avoids the loading=true flash on every page mount.
  const cached = session ? readCachedOrg(session.user.id) : null;
  const [org, setOrg] = useState<OrgMembership | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!session) {
      setOrg(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Only flip loading=true if we don't have cached data to show. If
    // we already painted from cache, the fresh fetch runs silently in
    // the background and the user never sees a loading state.
    const haveCache = !!readCachedOrg(session.user.id);
    if (!haveCache) setLoading(true);
    setError(null);

    (async () => {
      try {
        await ensureUserHasOrg();
        if (cancelled) return;

        const { data: members, error: mErr } = await supabase
          .from('org_members')
          .select('org_id, role')
          .eq('user_id', session.user.id)
          .order('joined_at', { ascending: true })
          .limit(1);
        if (mErr) throw mErr;
        if (!members || members.length === 0) {
          if (!cancelled) setOrg(null);
          return;
        }
        const m = members[0];

        const { data: orgRow, error: oErr } = await supabase
          .from('organisations')
          .select('name, logo_path, role, trades, risky_activities, profile_completed, enabled_features, disabled_features')
          .eq('id', m.org_id as string)
          .single();
        if (oErr) throw oErr;

        if (!cancelled) {
          const fresh: OrgMembership = {
            org_id: m.org_id as string,
            org_name: orgRow.name as string,
            role: m.role as Role,
            logo_path: (orgRow.logo_path as string | null) ?? null,
            org_role: ((orgRow as Record<string, unknown>).role as OrgRoleValue | null) ?? null,
            trades: (((orgRow as Record<string, unknown>).trades as string[] | null) ?? []),
            risky_activities: (((orgRow as Record<string, unknown>).risky_activities as string[] | null) ?? []),
            profile_completed: Boolean((orgRow as Record<string, unknown>).profile_completed),
            enabled_features: (((orgRow as Record<string, unknown>).enabled_features as string[] | null) ?? []),
            disabled_features: (((orgRow as Record<string, unknown>).disabled_features as string[] | null) ?? []),
          };
          setOrg(fresh);
          // Persist for stale-while-revalidate on next page mount.
          writeCachedOrg(session.user.id, fresh);
          // Tag Sentry reports with the org so a single error tells us
          // which customer is affected without a database lookup.
          setSentryOrg(m.org_id as string, orgRow.name as string);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [session, version]);

  return { org, loading, error, refresh };
}
